import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Dices, Copy, Plus, Trash2 } from 'lucide-react';
import {
  getMySpinWheel,
  saveSpinWheel,
  SpinWheel,
} from '../../../api/payment/spin-wheel.api';

const MAX_ITEMS = 12;

type Row = { label: string; weight: number };

/**
 * Cấu hình "Vòng quay" cho streamer.
 *
 * Các ô là NHIỆM VỤ streamer sẽ làm (hát 1 bài, chơi 1 ván với fan…) — cố ý KHÔNG
 * phải phần thưởng quy đổi được, để người xem đang trả cho một màn trình diễn chứ
 * không phải quay số trúng thưởng.
 */
const SpinWheelSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const { host, protocol } = window.location;
  const overlayUrl = `${protocol}//${host}/spin-wheel-screen/${userId}`;

  const [isActive, setIsActive] = useState(false);
  const [showOdds, setShowOdds] = useState(true);
  const [spinPrice, setSpinPrice] = useState<string>('');
  const [rows, setRows] = useState<Row[]>([
    { label: '', weight: 1 },
    { label: '', weight: 1 },
  ]);
  const [saving, setSaving] = useState(false);

  const apply = (w: SpinWheel) => {
    setIsActive(!!w.isActive);
    setShowOdds(!!w.showOdds);
    setSpinPrice(w.spinPrice ? `${w.spinPrice}` : '');
    if (w.items?.length) {
      setRows(w.items.map((i) => ({ label: i.label, weight: i.weight ?? 1 })));
    }
  };

  useEffect(() => {
    getMySpinWheel()
      .then((w) => {
        if (w?.exists) apply(w);
      })
      .catch(() => {
        /* chưa cấu hình lần nào -> để form mặc định */
      });
  }, []);

  const totalWeight = rows.reduce((s, r) => s + (Number(r.weight) || 0), 0);

  const setRow = (idx: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleSave = () => {
    if (saving) return;
    const items = rows
      .filter((r) => r.label.trim())
      .map((r) => ({ label: r.label.trim(), weight: Number(r.weight) || 1 }));

    if (isActive && items.length < 2) {
      toast.error('Cần ít nhất 2 ô nhiệm vụ để mở vòng quay.');
      return;
    }
    setSaving(true);
    saveSpinWheel({
      isActive,
      spinPrice: Number(spinPrice) || 0,
      showOdds,
      items,
    })
      .then((w) => {
        apply(w);
        toast.success('Đã lưu vòng quay');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu vòng quay thất bại');
      })
      .finally(() => setSaving(false));
  };

  // Mở overlay ở chế độ xem thử để streamer căn vị trí trong OBS trước khi lên sóng.
  const openPreview = () => {
    window.open(`${overlayUrl}?isTest=true`, 'popup', 'width=700,height=520,top=100,left=100');
  };

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link overlay');
    } catch {
      toast.error('Trình duyệt chặn sao chép — hãy bôi đen link rồi copy tay.');
    }
  };

  // KHÔNG để w-full trong class dùng chung: ở hàng nhiệm vụ nó đè mất w-20 của ô trọng số,
  // làm ô trọng số giãn hết cỡ còn ô nhập tên bị bóp còn ~26px (không đọc/sửa được).
  const inputCls =
    'h-11 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Dices size={20} className="text-blue-600" />
        Vòng quay
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Người xem trả Ca để quay, trúng ô nào thì bạn làm nhiệm vụ đó. Ghi nhiệm vụ bạn
          sẵn sàng làm (hát 1 bài, chơi 1 ván với fan…) — đây không phải phần thưởng quy
          đổi được. Tiền mỗi lượt quay vào ví như một lượt donate.
        </p>

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Mở vòng quay
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Tắt thì người xem không quay được và overlay không hiện gì.
            </span>
          </span>
        </label>

        <div className="flex flex-col gap-1">
          <label htmlFor="spin-price" className="text-sm">
            Giá 1 lượt quay (Ca)
          </label>
          <input
            id="spin-price"
            type="number"
            min={1}
            value={spinPrice}
            onChange={(e) => setSpinPrice(e.target.value)}
            placeholder="VD: 10000"
            className={`${inputCls} w-full`}
          />
        </div>

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showOdds}
            onChange={(e) => setShowOdds(e.target.checked)}
            className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Hiện tỉ lệ cho người xem
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Tắt thì người xem không xem được tỉ lệ trúng của từng ô.
            </span>
          </span>
        </label>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Danh sách nhiệm vụ</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Tỉ lệ là TRỌNG SỐ, không phải %. Tổng hiện tại: {totalWeight}
            </span>
          </div>

          {rows.map((row, idx) => {
            const pct =
              totalWeight > 0
                ? Math.round(((Number(row.weight) || 0) * 1000) / totalWeight) / 10
                : 0;
            return (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={row.label}
                  maxLength={60}
                  onChange={(e) => setRow(idx, { label: e.target.value })}
                  placeholder={`Nhiệm vụ ${idx + 1} (VD: Hát 1 bài)`}
                  className={`${inputCls} min-w-0 flex-1`}
                />
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={row.weight}
                  onChange={(e) => setRow(idx, { weight: Number(e.target.value) })}
                  className={`${inputCls} w-20 shrink-0`}
                  title="Trọng số"
                />
                <span className="w-14 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                  {pct}%
                </span>
                <button
                  type="button"
                  onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}
                  disabled={rows.length <= 1}
                  title="Xoá ô"
                  className="shrink-0 rounded-lg border border-[#C8CCD8] dark:border-slate-600 p-2 text-red-500 disabled:opacity-40"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}

          {rows.length < MAX_ITEMS && (
            <button
              type="button"
              onClick={() => setRows((p) => [...p, { label: '', weight: 1 }])}
              className="flex w-fit items-center gap-1 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-2 text-sm dark:text-white"
            >
              <Plus size={16} />
              Thêm nhiệm vụ
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">
            Link hiển thị vòng quay ( Add Webpage URL trong phần mềm Stream )
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
        </div>

        <div className="flex justify-between gap-3">
          <button
            type="button"
            onClick={openPreview}
            className="rounded-lg bg-[#39539E] px-4 py-2 text-base text-white"
          >
            Xem thử
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu vòng quay'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpinWheelSetting;
